/**
 * Sub-components for InvoiceDetailView
 */

import { Badge, Button } from '@elsapiens/ui'
import { Banknote, FileText, AlertCircle, Receipt as ReceiptIcon, Info, Undo2 } from 'lucide-react'

import { formatDisplayDate } from './invoices-helpers'
import { useBaseCurrency } from '../../hooks/use-currencies'
import { formatAmount } from '../../types/billing'
import { toSmallestUnit } from '../../utils/formatters'

import type { InvoicePaymentsResponse } from '../../services/payment-service'
import type { Invoice } from '../../types/billing'

interface InvoiceDetailsCardProps {
  invoice: Invoice
  isVendor: boolean
  invoiceCurrency: string
}

export function InvoiceDetailsCard({ invoice, isVendor }: InvoiceDetailsCardProps): JSX.Element {
  const baseCurrency = useBaseCurrency()
  return (
    <div className="bg-card border border-border rounded-lg el-px-md el-py-md">
      <h3 className="text-sm font-semibold text-foreground el-mb-sm flex items-center gap-1.5">
        <FileText className="w-4 h-4" />Invoice Details
      </h3>
      <div className="space-y-1.5 text-sm">
        <Row label="Invoice #" value={<span className="font-mono font-medium">{invoice.invoiceNumber}</span>} />
        <Row label="Date" value={formatDisplayDate(invoice.invoiceDate)} />
        {invoice.dueDate && <Row label="Due Date" value={formatDisplayDate(invoice.dueDate)} />}
        <Row label={isVendor ? 'Supplier' : 'Customer'} value={<span className="text-right max-w-[200px] truncate">{invoice.partyName}</span>} />
        {invoice.currency && invoice.currency !== baseCurrency && <Row label="Currency" value={invoice.currency} />}
        {invoice.exchangeRate && Number(invoice.exchangeRate) !== 1 && (
          <Row label="Exchange Rate" value={<span className="font-mono">{invoice.exchangeRate}</span>} />
        )}
        {invoice.poNumber && <Row label="PO #" value={<span className="font-mono">{invoice.poNumber}</span>} />}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between">
      <span className="text-foreground-secondary">{label}</span>
      <span>{value}</span>
    </div>
  )
}

interface ContextualHelpProps {
  status: string | undefined
  isPosted: boolean
  isWrittenOff: boolean
  isProforma: boolean
  isOverdue: boolean
  daysOverdue: number
  outstandingBalance: number
  totalAmount: number
}

interface HelpStateMatcher {
  match: (p: ContextualHelpProps) => boolean
  render: (p: ContextualHelpProps) => JSX.Element
}

const HELP_STATES: HelpStateMatcher[] = [
  {
    match: p => p.status === 'draft' && !p.isProforma,
    render: () => <p>This invoice is a <strong className="text-foreground">draft</strong>. Review the details and <strong className="text-foreground">Post</strong> it to finalize. Once posted, payments can be recorded against it.</p>,
  },
  {
    match: p => p.status === 'draft' && p.isProforma,
    render: () => <p>This is a <strong className="text-foreground">proforma invoice</strong>. Advance payments can be collected against it. Convert to a final invoice once goods/services are delivered.</p>,
  },
  {
    match: p => p.isPosted && p.isWrittenOff,
    render: () => <p>This invoice has been <strong className="text-foreground">written off</strong>. The remaining balance was cleared as bad debt or rounding. Use <strong className="text-foreground">Reverse Write-Off</strong> to undo this and restore the outstanding balance.</p>,
  },
  {
    match: p => p.isPosted && !p.isWrittenOff && p.outstandingBalance <= 0,
    render: () => <p>This invoice is <strong className="text-foreground">fully paid</strong>. All payments have been recorded and allocated.</p>,
  },
  {
    match: p => p.isPosted && !p.isWrittenOff && p.outstandingBalance > 0 && p.outstandingBalance < p.totalAmount,
    render: ({ isOverdue, daysOverdue }) => (
      <p>Partial payment received. Record remaining payments as they come in.{isOverdue && <> This invoice is <strong className="text-red-600">{daysOverdue} days overdue</strong>.</>} For small residual balances, use <strong className="text-foreground">Round Off</strong> or <strong className="text-foreground">Bad Debt</strong>.</p>
    ),
  },
  {
    match: p => p.isPosted && !p.isWrittenOff && p.outstandingBalance >= p.totalAmount,
    render: ({ isOverdue, daysOverdue }) => (
      <p>This invoice is posted and awaiting payment.{isOverdue && <> It is <strong className="text-red-600">{daysOverdue} days overdue</strong>.</>} Once payment is received, record it to update the outstanding balance.</p>
    ),
  },
  {
    match: p => p.status === 'voided',
    render: () => <p>This invoice has been <strong className="text-foreground">voided</strong> and is no longer active. No further actions can be taken.</p>,
  },
]

export function ContextualHelpText(props: ContextualHelpProps): JSX.Element {
  for (const state of HELP_STATES) {
    if (state.match(props)) {return state.render(props)}
  }
  return <p />
}

export function ContextualHelp(props: ContextualHelpProps): JSX.Element {
  return (
    <div className="rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 el-px-sm el-py-sm text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
      <div className="flex gap-2">
        <Info className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
        <ContextualHelpText {...props} />
      </div>
    </div>
  )
}

interface PaymentSummaryCardProps {
  isPosted: boolean
  isWrittenOff: boolean
  isOverdue: boolean
  isWriteOffSaving: boolean
  daysOverdue: number
  totalAmount: number
  totalPaid: number
  outstandingBalance: number
  invoiceCurrency: string
  confirmingAction: 'rounding' | 'bad_debt' | null
  confirmTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  setConfirmingAction: (a: 'rounding' | 'bad_debt' | null) => void
  setIsWriteOffConfirmVisible: (v: boolean) => void
  setWriteOffType: (t: 'rounding' | 'bad_debt') => void
}

function resolvePaymentBadgeVariant(isWrittenOff: boolean, outstandingBalance: number, totalAmount: number): 'error' | 'success' | 'warning' | 'secondary' {
  if (isWrittenOff) { return 'error' }
  if (outstandingBalance <= 0) { return 'success' }
  if (outstandingBalance < totalAmount) { return 'warning' }
  return 'secondary'
}

function resolvePaymentBadgeLabel(isWrittenOff: boolean, outstandingBalance: number, totalAmount: number): string {
  if (isWrittenOff) { return 'Written Off' }
  if (outstandingBalance <= 0) { return 'Paid' }
  if (outstandingBalance < totalAmount) { return 'Partial' }
  return 'Unpaid'
}

export function PaymentSummaryCard(p: PaymentSummaryCardProps): JSX.Element {
  const badgeVariant = resolvePaymentBadgeVariant(p.isWrittenOff, p.outstandingBalance, p.totalAmount)
  const badgeLabel = resolvePaymentBadgeLabel(p.isWrittenOff, p.outstandingBalance, p.totalAmount)

  return (
    <div className="bg-card border border-border rounded-lg el-px-md el-py-md">
      <h3 className="text-sm font-semibold text-foreground el-mb-sm flex items-center gap-1.5">
        <ReceiptIcon className="w-4 h-4" />Payment Summary
      </h3>
      <div className="flex flex-col el-gap-sm text-sm">
        <Row label="Invoice Total" value={<span className="font-mono font-medium">{formatAmount(p.totalAmount, p.invoiceCurrency)}</span>} />
        <Row label="Received" value={<span className="font-mono font-medium text-green-600">{formatAmount(p.totalPaid, p.invoiceCurrency)}</span>} />
        <div className="border-t border-border pt-2 flex justify-between items-center">
          <span className="text-foreground-secondary font-medium">Outstanding</span>
          <span className="font-mono font-semibold">{formatAmount(p.outstandingBalance, p.invoiceCurrency)}</span>
        </div>
        {p.isPosted && (
          <div className="flex justify-end gap-1.5">
            <Badge size="sm" variant={badgeVariant}>{badgeLabel}</Badge>
            {p.isOverdue && <Badge size="sm" variant="error">{p.daysOverdue}d overdue</Badge>}
          </div>
        )}
      </div>
    </div>
  )
}

interface WriteOffReverseCardProps {
  isWriteOffSaving: boolean
  onReverseWriteOff: () => void
}

export function WriteOffReverseCard({ isWriteOffSaving, onReverseWriteOff }: WriteOffReverseCardProps): JSX.Element {
  return (
    <div className="bg-card border border-border rounded-lg el-px-md el-py-md">
      <h3 className="text-sm font-semibold text-foreground el-mb-sm">Actions</h3>
      <div className="flex gap-2 p-3 rounded-lg bg-red-50 border border-red-200 el-mb-sm">
        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
        <p className="text-xs text-red-700">
          This invoice has been written off. The balance was cleared via a write-off entry. You can reverse this to restore the outstanding balance.
        </p>
      </div>
      <Button disabled={isWriteOffSaving} size="sm" variant="secondary" onClick={onReverseWriteOff}>
        <Undo2 className="w-3.5 h-3.5 mr-1" />
        {isWriteOffSaving ? 'Reversing...' : 'Reverse Write-Off'}
      </Button>
    </div>
  )
}

interface WriteOffActionsCardProps {
  invoiceCurrency: string
  outstandingBalance: number
  isWriteOffSaving: boolean
  confirmingAction: 'rounding' | 'bad_debt' | null
  confirmTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  setConfirmingAction: (a: 'rounding' | 'bad_debt' | null) => void
  setIsWriteOffConfirmVisible: (v: boolean) => void
  setWriteOffType: (t: 'rounding' | 'bad_debt') => void
}

function useWriteOffActionHandler(p: WriteOffActionsCardProps): (type: 'rounding' | 'bad_debt') => void {
  return (type: 'rounding' | 'bad_debt') => {
    if (p.confirmingAction === type) {
      if (p.confirmTimerRef.current) { clearTimeout(p.confirmTimerRef.current) }
      p.setConfirmingAction(null)
      p.setWriteOffType(type)
      p.setIsWriteOffConfirmVisible(true)
    } else {
      p.setConfirmingAction(type)
      if (p.confirmTimerRef.current) { clearTimeout(p.confirmTimerRef.current) }
      p.confirmTimerRef.current = setTimeout(() => p.setConfirmingAction(null), 3000)
    }
  }
}

export function WriteOffActionsCard(p: WriteOffActionsCardProps): JSX.Element {
  const roundOffLimit = toSmallestUnit(10, p.invoiceCurrency)
  const canRoundOff = p.outstandingBalance <= roundOffLimit
  const handleActionClick = useWriteOffActionHandler(p)

  return (
    <div className="bg-card border border-border rounded-lg el-px-md el-py-md">
      <h3 className="text-sm font-semibold text-foreground el-mb-sm">Actions</h3>
      <div className="flex el-gap-control-md">
        {canRoundOff && (
          <Button
            disabled={p.isWriteOffSaving}
            size="sm"
            variant={p.confirmingAction === 'rounding' ? 'primary' : 'secondary'}
            onClick={() => handleActionClick('rounding')}
          >
            {p.confirmingAction === 'rounding' ? 'Confirm Round Off?' : 'Round Off'}
          </Button>
        )}
        <Button
          disabled={p.isWriteOffSaving}
          size="sm"
          variant={p.confirmingAction === 'bad_debt' ? 'destructive' : 'secondary'}
          onClick={() => handleActionClick('bad_debt')}
        >
          <AlertCircle className="w-3.5 h-3.5 mr-1" />
          {p.confirmingAction === 'bad_debt' ? 'Confirm Bad Debt?' : 'Bad Debt'}
        </Button>
      </div>
      {!canRoundOff && (
        <p className="text-xs text-foreground-secondary el-mt-sm">
          Round Off is available when the outstanding balance is ≤ 10.00.
        </p>
      )}
    </div>
  )
}

interface PaymentHistoryCardProps {
  isLoading: boolean
  allocations: InvoicePaymentsResponse['allocations']
  invoiceCurrency: string
}

export function PaymentHistoryCard({ isLoading, allocations, invoiceCurrency }: PaymentHistoryCardProps): JSX.Element {
  return (
    <div className="bg-card border border-border rounded-lg el-px-md el-py-md">
      <h3 className="text-sm font-semibold text-foreground el-mb-sm flex items-center gap-1.5">
        <Banknote className="w-4 h-4" />Payment History
      </h3>
      {isLoading && <div className="text-sm text-foreground-secondary animate-pulse">Loading...</div>}
      {!isLoading && allocations.length > 0 && (
        <div className="flex flex-col el-gap-sm">
          {allocations.map((alloc) => (
            <div key={alloc.id} className="flex justify-between items-center text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
              <div>
                <div className="font-medium text-foreground">{formatAmount(Number(alloc.allocatedAmount), invoiceCurrency)}</div>
                <div className="text-xs text-foreground-secondary">{formatDisplayDate(alloc.createdAt)}</div>
              </div>
              {alloc.notes && <span className="text-xs text-foreground-secondary max-w-[140px] truncate">{alloc.notes}</span>}
            </div>
          ))}
        </div>
      )}
      {!isLoading && allocations.length === 0 && (
        <div className="text-center el-py-md">
          <FileText className="w-8 h-8 text-foreground-secondary/40 mx-auto el-mb-sm" />
          <p className="text-sm text-foreground-secondary">No payments recorded</p>
        </div>
      )}
    </div>
  )
}

export interface PaymentPanelProps {
  invoice: Invoice
  isVendor: boolean
  isProforma: boolean
  isPosted: boolean
  isWrittenOff: boolean
  isOverdue: boolean
  isWriteOffSaving: boolean
  isInvoicePaymentsLoading: boolean
  daysOverdue: number
  totalAmount: number
  totalPaid: number
  outstandingBalance: number
  invoiceCurrency: string
  allocations: InvoicePaymentsResponse['allocations']
  writeOffType: 'rounding' | 'bad_debt'
  confirmingAction: 'rounding' | 'bad_debt' | null
  confirmTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  setConfirmingAction: (a: 'rounding' | 'bad_debt' | null) => void
  setIsWriteOffConfirmVisible: (v: boolean) => void
  setWriteOffType: (t: 'rounding' | 'bad_debt') => void
  onReverseWriteOff: () => void
}

function WriteOffSection(p: PaymentPanelProps): JSX.Element | null {
  if (p.isPosted && p.isWrittenOff) {
    return <WriteOffReverseCard isWriteOffSaving={p.isWriteOffSaving} onReverseWriteOff={p.onReverseWriteOff} />
  }
  if (p.isPosted && !p.isWrittenOff && p.outstandingBalance > 0) {
    return (
      <WriteOffActionsCard
        confirmingAction={p.confirmingAction}
        confirmTimerRef={p.confirmTimerRef}
        invoiceCurrency={p.invoiceCurrency}
        isWriteOffSaving={p.isWriteOffSaving}
        outstandingBalance={p.outstandingBalance}
        setConfirmingAction={p.setConfirmingAction}
        setIsWriteOffConfirmVisible={p.setIsWriteOffConfirmVisible}
        setWriteOffType={p.setWriteOffType}
      />
    )
  }
  return null
}

export function PaymentPanel(p: PaymentPanelProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-md">
      <InvoiceDetailsCard invoice={p.invoice} invoiceCurrency={p.invoiceCurrency} isVendor={p.isVendor} />
      <ContextualHelp
        daysOverdue={p.daysOverdue}
        isOverdue={p.isOverdue}
        isPosted={p.isPosted}
        isProforma={p.isProforma}
        isWrittenOff={p.isWrittenOff}
        outstandingBalance={p.outstandingBalance}
        status={p.invoice.status}
        totalAmount={p.totalAmount}
      />
      <PaymentSummaryCard
        confirmingAction={p.confirmingAction}
        confirmTimerRef={p.confirmTimerRef}
        daysOverdue={p.daysOverdue}
        invoiceCurrency={p.invoiceCurrency}
        isOverdue={p.isOverdue}
        isPosted={p.isPosted}
        isWriteOffSaving={p.isWriteOffSaving}
        isWrittenOff={p.isWrittenOff}
        outstandingBalance={p.outstandingBalance}
        setConfirmingAction={p.setConfirmingAction}
        setIsWriteOffConfirmVisible={p.setIsWriteOffConfirmVisible}
        setWriteOffType={p.setWriteOffType}
        totalAmount={p.totalAmount}
        totalPaid={p.totalPaid}
      />
      <WriteOffSection {...p} />
      <PaymentHistoryCard
        allocations={p.allocations}
        invoiceCurrency={p.invoiceCurrency}
        isLoading={p.isInvoicePaymentsLoading}
      />
    </div>
  )
}
